import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "@/store";
import {
  fetchEntries,
  fetchActive,
  startTimer,
  stopTimer,
  createEntry,
  deleteEntry,
} from "@/store/slices/trackerSlice";
import { fetchProjects } from "@/store/slices/projectSlice";
import { useNotification } from "@/context/NotificationContext";

export function useTracker() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list, active, loading } = useAppSelector((s) => s.tracker);
  const projects = useAppSelector((s) => s.projects.list);
  const { notify } = useNotification();

  const [desc, setDesc] = useState("");
  const [projectId, setProjectId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualDesc, setManualDesc] = useState("");
  const [manualProject, setManualProject] = useState("");
  const [manualFrom, setManualFrom] = useState("");
  const [manualThrough, setManualThrough] = useState("");
  const [manualBillable, setManualBillable] = useState(true);

  useEffect(() => {
    dispatch(fetchEntries());
    dispatch(fetchActive());
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => {}, 1000);
    return () => clearInterval(iv);
  }, [active]);

  const handleStart = async () => {
    if (!projectId) return;
    await dispatch(
      startTimer({ description: desc || undefined, projectId }),
    ).unwrap();
    setDesc("");
    setProjectId("");
  };

  const handleStop = async () => {
    if (!active) return;
    await dispatch(stopTimer(active.id)).unwrap();
    notify(t("common.success"), "success");
    dispatch(fetchEntries());
  };

  const handleManualCreate = async () => {
    await dispatch(
      createEntry({
        description: manualDesc || undefined,
        projectId: manualProject,
        from: new Date(manualFrom).toISOString(),
        through: new Date(manualThrough).toISOString(),
        billable: manualBillable,
      }),
    ).unwrap();
    notify(t("common.success"), "success");
    setManualDesc("");
    setManualProject("");
    setManualFrom("");
    setManualThrough("");
    setDialogOpen(false);
  };

  const handleDelete = (id: string) => {
    dispatch(deleteEntry(id));
  };

  const completedEntries = list.filter((e: any) => e.through);

  const canStartTimer = !!projectId;
  const canCreateManual = !!manualProject && !!manualFrom && !!manualThrough;

  return {
    list,
    active,
    loading,
    projects,
    completedEntries,
    desc,
    setDesc,
    projectId,
    setProjectId,
    dialogOpen,
    setDialogOpen,
    manualDesc,
    setManualDesc,
    manualProject,
    setManualProject,
    manualFrom,
    setManualFrom,
    manualThrough,
    setManualThrough,
    manualBillable,
    setManualBillable,
    handleStart,
    handleStop,
    handleManualCreate,
    handleDelete,
    canStartTimer,
    canCreateManual,
  };
}
